import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import {
  type DeliveryListRow,
  type DeliveryPage,
  isDeliveryStatusFinal,
} from '@/features/delivery';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

const DELIVERIES = '/v1/deliveries';

export interface OpenDeliveriesResult {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly data: readonly DeliveryListRow[];
  readonly refetch: () => void;
}

/**
 * The tenant's open deliveries, newest first (SPRINT07.md's own phrasing for `TrackingRoute`).
 *
 * Reads `GET /v1/deliveries` directly — the tenant-wide listing added after SPRINT07.md's own
 * "Agreed HTTP contract" table shipped, closing the same N+1 shape Sprint 6 closed for
 * payments with `GET /v1/payments` (see that module's own `usePaymentsQuery` for the
 * precedent this one repeats). This screen used to compose itself from `GET /v1/orders` plus
 * one `GET /v1/orders/{order_id}/delivery` call per loaded order, bounded only by how many
 * orders happened to be loaded; a single request now serves it regardless.
 *
 * The route carries no `establishment_id` filter — the same gap `/v1/payments` has — so this
 * reads every establishment's open deliveries rather than only the one `useCurrentEstablishment`
 * would have picked. That is a strictly wider, and more correct, reading of "the tenant's open
 * deliveries" than the composition it replaces ever gave: harmless today since every demo
 * tenant has exactly one establishment (see that hook's own comment on why it exists at all).
 *
 * "Open" is still decided client-side: the endpoint's own `status` filter narrows to exactly
 * one status, not to "every non-final one", so the unfiltered page is narrowed here with
 * `isDeliveryStatusFinal` instead of firing one request per open status.
 */
export function useOpenDeliveriesQuery(): OpenDeliveriesResult {
  const { tenantId } = useAuth();

  const query = useQuery({
    queryKey: [...queryKeys.tracking(tenantId ?? 'sem-tenant'), 'open'],
    queryFn: ({ signal }) => api.get<DeliveryPage>(DELIVERIES, { signal }),
    enabled: tenantId !== null,
  });

  // `items` is declared required on the real schema, but still guarded here the same way
  // `features/delivery/queries.ts` guards `MovementExportPage.items` — a response the
  // compiler trusts today is still a response a future optional-flagged field change can break.
  const data = (query.data?.items ?? []).filter((row) => !isDeliveryStatusFinal(row.status));

  return {
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    data,
    refetch: () => {
      void query.refetch();
    },
  };
}
